import {
    Box,
    Heading,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    Button,
    Image,
    Link,
    useDisclosure
} from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { useParams } from 'react-router-dom';
import logo from '../../assets/pm-logo-white.svg';
import Footer from '../../Footer';
import NavBar from '../NavBar';
import './Jobs.scss';
import { generateGuid } from '../../shared/util';
import { useGetJobBoardJobByIdQuery, useMeQuery, useCreateApplicationForJobMutation } from '../../generated/graphql';
import { unslugifyItem, unslugifyList } from '../../shared/unslugify';

function JobPost({ studentView }: { studentView: boolean }) {
    let params = useParams();
    const { data: jobBoardJob, loading } = useGetJobBoardJobByIdQuery({
        variables: { jobId: params.jobId! }
    });

    const jobPost = jobBoardJob?.getJobBoardJobById;

    if (loading) {
        return <div> Loading</div>;
    }
    if (!jobPost) {
        return <div>Job Not Found</div>;
    }

    return (
        <>
            {studentView ? <NavBar /> : null}

            {/* Job Post Hero */}
            <Box className="jobPostHero">
                <Box
                    className="employerLogoWrapper"
                    maxWidth="80px"
                    borderRadius="12px"
                    overflow="hidden"
                    mb="20px"
                >
                    <Image
                        // Older logos or logos uploaded through webflow stored in
                        // employer-logo.url.  Ones from typeform stored at just employer-logo.
                        src={jobPost.company?.logo as string}
                        alt="Employer Logo"
                    ></Image>
                </Box>
                <Heading as="h1" className="jobPostTitle">
                    {jobPost.title}
                </Heading>
                <Text fontSize={'20px'} w={'100%'}>
                    at {jobPost.company?.title}
                </Text>
            </Box>

            {/* Job Post Content */}
            <Box className="jobPostGrid" display={{ lg: 'grid', md: 'block', sm: 'block' }}>
                {studentView ? (
                    <ApplyButton jobId={jobPost.id} jobSlug={jobPost.slug} isMobile />
                ) : null}
                <Box>
                    <Heading fontSize="28px" marginBottom="12px">
                        Company Description
                    </Heading>
                    <Text marginBottom="40px">{jobPost.company?.bio}</Text>
                    {jobPost.description && (
                        <Heading fontSize="28px" marginBottom="12px">
                            What you'll be doing
                        </Heading>
                    )}
                    <div
                        style={{
                            marginLeft: '40px',
                            marginBottom: '40px',
                            lineHeight: '200%'
                        }}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(jobPost.description as string)
                        }}
                    />
                    {jobPost.requirements && (
                        <Heading fontSize="28px" marginBottom="12px">
                            What you'll need
                        </Heading>
                    )}
                    <div
                        style={{
                            marginLeft: '40px',
                            marginBottom: '40px',
                            lineHeight: '200%'
                        }}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(jobPost.requirements as string)
                        }}
                    />
                    {Boolean(jobPost.company?.perks.length) && (
                        <Heading fontSize="28px" marginBottom="12px">
                            What's in it for you
                        </Heading>
                    )}
                    <div
                        style={{ marginLeft: '40px', marginBottom: '40px' }}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(jobPost.company?.perks[0] as string)
                        }}
                    />
                </Box>
                <Box>
                    {/* Job Details */}
                    {studentView ? <ApplyButton jobId={jobPost.id} jobSlug={jobPost.slug} isMobile={false}/> : null}
                    <Box
                        borderStyle="solid"
                        borderColor="midnight.50"
                        borderWidth="1px"
                        borderRadius="20px"
                        padding="20px"
                        height="fit-content"
                        marginTop="28px"
                    >
                        <Heading fontWeight="600" fontSize="20px">
                            Job Details
                        </Heading>
                        <SidebarHeading>Job Type</SidebarHeading>
                        <Text>{unslugifyList(jobPost.types)}</Text>
                        <SidebarHeading>Industry</SidebarHeading>
                        <Text>{jobPost.category && unslugifyItem(jobPost.category)}</Text>
                        <SidebarHeading>Location</SidebarHeading>
                        <Text>{jobPost.locations && unslugifyList(jobPost.locations)}</Text>
                        <SidebarHeading>Compensation</SidebarHeading>
                        <Text>{jobPost.compensation}</Text>
                        <SidebarHeading>GPA Requirements</SidebarHeading>
                        <Text>{jobPost.desired_gpa}</Text>
                        <SidebarHeading>Visa Sponsorship</SidebarHeading>
                        <Text>{jobPost.visa_sponsorship}</Text>
                    </Box>
                </Box>
            </Box>
            <Footer></Footer>
        </>
    );
}

export default JobPost;

function SidebarHeading({ ...props }) {
    return (
        <Heading
            size="sm"
            fontWeight="600"
            marginTop="28px"
            marginBottom="4px"
            {...props}
        ></Heading>
    );
}

function ApplyButton({ jobId, jobSlug, isMobile} : { jobId: string, jobSlug: string, isMobile: boolean }) {
    const { data: userData, loading } = useMeQuery();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const userApplication = userData?.me?.applications?.find(app => app?.job_id === jobId);
    const [applyToJob, { data: applicationData, loading: isApplicationProcessing }] = useCreateApplicationForJobMutation({
        variables: { slug: jobSlug },
        onCompleted: () => {
            onClose();
        }
    });

    const userHasApplied = userApplication || applicationData?.createApplicationForJob?.success;

    let applyButtonText = '';
    if (isApplicationProcessing) {
        applyButtonText = 'Processing';
    } else if (userHasApplied) {
        applyButtonText = 'Applied';
    } else if (loading) {
        applyButtonText = 'Loading';
    } else {
        applyButtonText = 'Apply now';
    }

    return (
        <>
            <Button
                variant="primary"
                isDisabled={Boolean(userHasApplied) || loading}
                bg={userHasApplied ? 'green' : 'brand.500'}
                _hover={{
                    backgroundColor: userHasApplied ? 'green' : 'brand.700'
                }}
                onClick={onOpen}
                display={isMobile ? { base: 'flex', lg: 'none' } : { base: 'none', lg: 'flex' }}
                marginBottom={isMobile ? "20px" : 'none'}
            >
                {applyButtonText}
            </Button>

            {/* This modal is for applying with an already created profile */}
            <Modal isOpen={isOpen && Boolean(userData?.me.profile?.id)} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Ready To Apply?</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        We'll send this employer your PathMatch profile, where they can reach out
                        for an interview if they're interested.
                    </ModalBody>
                    <ModalFooter flexDirection="column">
                        <Button
                            variant="primary"
                            margin="10px"
                            onClick={() => {
                                applyToJob();
                            }}
                        >
                            <Image src={logo} h="15px" marginRight="10px" />
                            Apply with PathMatch
                        </Button>
                        <Button onClick={onClose}>Nevermind</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* This modal is for users who need to create a profile */}
            <Modal isOpen={isOpen && !userData?.me.profile?.id} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>First, Create your PathMatch Profile</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        Your PathMatch profile is what we will share with employers when you apply.
                        You need to create this first before applying.
                    </ModalBody>
                    <ModalFooter flexDirection="column">
                        <Link
                            href={
                                'https://pathmatch.typeform.com/to/Z6QZ76z3?typeform-source=app.pathmatch.com' +
                                `#profileId=${generateGuid()}&uid=${userData?.me.id}`
                            }
                        >
                            <Button
                                variant="primary"
                                margin="10px"
                                onClick={() => {
                                    onClose();
                                }}
                            >
                                <Image src={logo} h="15px" marginRight="10px" />
                                Create Profile
                            </Button>
                        </Link>
                        <Button onClick={onClose}>Nevermind</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}
